<script setup lang="ts">
import {
  Tabs,
  type ITabItemProps
} from "@frontiers-vue3/public-pages";
import MainHeader from "~/components/templates/v1/MainHeader/MainHeader.vue";
import RTSidePanel from "~/components/templates/v1/RTSidePanel/RTSidePanel.vue";

const {
  showRtImpactTab,
  isImpactMetricsVisible,
  isAboutThisResearchTopicsVisible
} = useRuntimeConfig().public;
const terminology = useTerminologyStore();
const store = useMain();
const route = useRoute();

onBeforeMount(() => {
  store.fetchBreadcrumbs(store.currentResearchTopic);
});

const rtDetails = store.currentResearchTopic;

const selectedTab = ref<number>(0);

const tabArticle = ref<ITabItemProps>({
  label: terminology.getTerm("articles"),
  counter: store.totalArticles
});
const tabAuthors = ref<ITabItemProps>({
  label: terminology.getTerm("authors"),
  counter: store.totalAuthors
});

const tabs = [tabArticle.value, tabAuthors.value];

watch(
  () => [store.totalArticles, store.totalAuthors],
  () => {
    tabArticle.value.counter = store.totalArticles;
    tabAuthors.value.counter = store.totalAuthors;
  }
);

const tabSelected = (): void => {
  const tabValue = tabs[selectedTab.value].label.toLowerCase();
  if (tabValue !== "overview") {
    navigateTo(
      `/research-topics/${route.params.rtId}/${rtDetails?.slug}/${tabs[
        selectedTab.value
      ].label.toLowerCase()}`
    );
  } else {
    navigateTo(`/research-topics/${route.params.rtId}/${rtDetails?.slug}`);
  }
};

const impactNumbers: Record<string, number> = {
  [`${terminology.getTerm("topicTitle")} views`]:
    store.currentResearchTopic.impactV5?.views || 0,
  [`${terminology.getTerm("article")} views`]:
    store.currentResearchTopic.impactV5?.articleViews || 0,
  [`${terminology.getTerm("article")} downloads`]:
    store.currentResearchTopic.impactV5?.downloads || 0
};

const updateSelectedTabIndex = (): void => {
  const rtTab = (route.name as string).split("research-topics-rtId-rtSlug-")[1];

  if (!rtTab) {
    selectedTab.value = 0;
    return;
  }

  if (rtTab === "Impact" && !showRtImpactTab) {
    selectedTab.value = 0;
    tabSelected();
    return;
  }

  const index = tabs.findIndex(
    (tab) => tab.label.toLowerCase() === rtTab.toLowerCase()
  );

  selectedTab.value = index >= 0 ? index : 0;
};

if (
  isImpactMetricsVisible &&
  showRtImpactTab &&
  !tabs.find((tab) => tab.label === "Impact")
) {
  tabs.push({ label: "Impact", counter: 0 });
}

if (
  isAboutThisResearchTopicsVisible &&
  !tabs.find((tab) => tab.label === "Overview")
) {
  tabs.unshift({
    label: "Overview",
    counter: 0
  });
}

updateSelectedTabIndex();

watch(
  () => route.path,
  () => updateSelectedTabIndex()
);
</script>

<template>
  <div>
    <MainHeader
      :is-submit-manuscript-enabled="store.getIsSubmitManuscriptEnabled" />

    <Tabs
      id="MainTabs"
      v-model="selectedTab"
      :tabs="tabs"
      size="md"
      :hide-divider="true"
      @update:model-value="tabSelected">
      <template #panel="panelProps">
        <div
          v-bind="{ ...panelProps }"
          :class="`content content-${tabs[selectedTab].label}`">
          <div
            id="main-content"
            class="content__main">
            <slot />
          </div>
          <RTSidePanel
            :impact-numbers="impactNumbers"
            :is-submit-manuscript-enabled="
              store.getIsSubmitManuscriptEnabled
            " />
        </div>
      </template>
    </Tabs>
  </div>
</template>

<style lang="scss">
.content {
  display: flex;
  position: relative;
  justify-content: space-between;
  padding: 48px 0;
  @include generalPageWrapper;

  @media #{$break-md} {
    flex-wrap: wrap;
  }

  @media #{$break-sm} {
    width: var(--layout-col-21);
  }

  @media #{$break-xs} {
    padding: 40px 0;
    width: var(--layout-col-24);
  }

  &__main {
    flex: 1;
    width: 100%;
  }
}
.Tabs {
  &__scrollWrapper {
    background-color: $white;
    border-bottom: 0.0625rem solid $grey40;
  }
  &__slidingPanel {
    width: var(--layout-col-20);
    margin-left: auto;
    margin-right: auto;

    @media #{$break-sm} {
      width: var(--layout-col-21);
    }

    @media #{$break-xs} {
      width: var(--layout-col-24);
    }
  }
  &__tablist {
    @include generalPageWrapper;
  }
}
</style>
